import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import axiosInstance from '../axiosInstance';

const BASE_INTERVAL = 15000; // 15s between polls when healthy
const MAX_INTERVAL = 120000; // 2min cap on 429 backoff

// Origin-scoped names. Both the lock + the channel are isolated per origin
// by the browser, so ccbill.fanstext.com and lemonsocial.com (etc.) don't
// step on each other even if a user has tabs open on both at once.
const LOCK_NAME = 'ofengine:toast-poll-leader';
const CHANNEL_NAME = 'ofengine:toast-poll';

const typeIcons = {
  tip: '💰',
  follow: '👤',
  subscription: '⭐',
  like: '❤️',
  comment: '💬',
  comment_reply: '💬',
  comment_like: '❤️',
  post_unlock: '🔓',
  message_unlock: '🔓',
  top_fan: '🏆',
  livestream: '🔴',
  creator_verification: '🎉',
};

// Renders a single notification as a toast OR dispatches the videocall:refresh
// event for incoming calls. Shared by both the leader (which polls directly)
// and followers (which receive notifications via BroadcastChannel).
const showNotificationToast = (notif, durationSec) => {
  if (notif.type === 'incoming_call') {
    window.dispatchEvent(new CustomEvent('videocall:refresh'));
    return;
  }
  const icon = typeIcons[notif.type] || '🔔';
  const fromName = notif.from_user?.display_name || notif.from_user?.username || 'Someone';
  toast(notif.message || `${fromName} sent you a notification`, {
    duration: (durationSec || 5) * 1000,
    icon,
    style: {
      borderRadius: '10px',
      background: '#333',
      color: '#fff',
      fontSize: '14px',
    },
  });
};

/**
 * ToastNotificationManager
 *
 * Coordinated cross-tab polling for /notifications/toast-poll.
 *
 * Problem this solves: without coordination, every open tab independently
 * polls every 15 seconds. Three tabs of the same user account = 12 req/min,
 * which trips the backend's `RateLimiter::for('polling')` cap of 10/min and
 * surfaces as a 429 to the user.
 *
 * Strategy:
 *   1. `navigator.locks.request()` elects ONE leader tab per origin at a
 *      time. Only the leader hits the API.
 *   2. `BroadcastChannel` relays each successful poll's notifications from
 *      the leader to every follower tab, which display the same toasts in
 *      their own window. No duplicate fetches, no missed notifications.
 *   3. When the leader tab closes, the next-queued tab automatically picks
 *      up leadership — the Web Locks API handles that handoff for us.
 *
 * Browser support: Web Locks + BroadcastChannel are both available in
 * Chrome 69+, Firefox 96+, Safari 15.4+, Edge 79+. On anything older the
 * component silently falls back to the original per-tab polling loop.
 */
const ToastNotificationManager = () => {
  const token = useSelector((state) => state.auth.token);

  useEffect(() => {
    if (!token) return undefined;

    const supportsCoordination =
      typeof BroadcastChannel !== 'undefined' &&
      typeof navigator !== 'undefined' &&
      navigator.locks &&
      typeof navigator.locks.request === 'function';

    // All polling state lives inside this effect closure so logout (which
    // drops the token and re-runs the effect) resets cleanly.
    let cancelled = false;
    let visible = document.visibilityState === 'visible';
    let interval = BASE_INTERVAL;
    let sinceId = 0;
    let initialized = false;
    let pollTimeoutId = null;
    let releaseLeadership = null;
    let channel = null;

    const performPoll = async () => {
      if (cancelled || !visible) return;
      try {
        const res = await axiosInstance.get('/notifications/toast-poll', {
          params: { since_id: sinceId },
        });
        if (!res.data?.success) return;
        interval = BASE_INTERVAL;

        const { data: notifications, latest_id, duration } = res.data;
        if (latest_id > sinceId) sinceId = latest_id;

        // First poll after taking leadership returns "since=0" results, which
        // are already-seen notifications. Skip the display on that first batch.
        if (!initialized) {
          initialized = true;
          return;
        }

        // Display in this tab AND broadcast to all follower tabs.
        notifications.forEach((n) => showNotificationToast(n, duration));
        if (channel) {
          try {
            channel.postMessage({ type: 'notifications', notifications, duration });
          } catch (_) { /* channel closed mid-broadcast — ignore */ }
        }
      } catch (err) {
        if (err?.response?.status === 429) {
          // Should be rare now that only the leader polls, but keep the
          // exponential backoff as a safety net.
          interval = Math.min(interval * 2, MAX_INTERVAL);
        }
      }
    };

    const stopPolling = () => {
      if (pollTimeoutId) {
        clearTimeout(pollTimeoutId);
        pollTimeoutId = null;
      }
    };

    const scheduleNextPoll = () => {
      if (cancelled) return;
      pollTimeoutId = setTimeout(async () => {
        await performPoll();
        scheduleNextPoll();
      }, interval);
    };

    const startPolling = () => {
      performPoll().then(scheduleNextPoll);
    };

    const handleVisibility = () => {
      visible = document.visibilityState === 'visible';
      // Leader (only) reacts to its own tab becoming visible by kicking a
      // fresh poll. Followers ignore visibility — they just display whatever
      // the leader broadcasts.
      if (visible && releaseLeadership) {
        interval = BASE_INTERVAL;
        stopPolling();
        startPolling();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    if (!supportsCoordination) {
      // Old browser fallback: per-tab polling, same as before this refactor.
      startPolling();
      return () => {
        cancelled = true;
        stopPolling();
        document.removeEventListener('visibilitychange', handleVisibility);
      };
    }

    // Follower wiring: listen for notifications broadcast by the leader.
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.addEventListener('message', (e) => {
      const msg = e.data || {};
      if (msg.type === 'notifications' && Array.isArray(msg.notifications)) {
        msg.notifications.forEach((n) => showNotificationToast(n, msg.duration));
      }
    });

    // Leader race. Whichever tab wins the exclusive lock polls; everyone
    // else stays queued inside this same .request() call until the leader
    // releases. The leader releases by either: (a) resolving the inner
    // promise via releaseLeadership(), or (b) closing its tab — at which
    // point the browser fires the lock release automatically.
    navigator.locks.request(LOCK_NAME, { mode: 'exclusive' }, () => {
      if (cancelled) return undefined;
      return new Promise((resolve) => {
        releaseLeadership = () => {
          releaseLeadership = null;
          stopPolling();
          resolve();
        };
        startPolling();
      });
    });

    return () => {
      cancelled = true;
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
      if (releaseLeadership) releaseLeadership();
      if (channel) channel.close();
    };
  }, [token]);

  return null;
};

export default ToastNotificationManager;
